import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'

import { Spinner } from '@/components/ui'
import { Button } from '@/components/ui/button'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer'
import { useInfinityLoadMore } from '@/features/user-management/hooks/useInfinityLoadMore'
import EmptyHistoryImage from '@/public/images/empty-history.svg'
import { EntityAuditLogEntry } from '@/schema'

import { groupByDate } from '../utils'

import AuditCard from './AuditCard'

const EmptyState: React.FC = () => {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col items-center justify-center flex-1 gap-3 px-6 py-16 text-center">
      <img
        src={EmptyHistoryImage}
        alt="No history available"
        className="w-40 h-40 object-contain"
      />
      <p className="text-md font-semibold text-primary-900 mb-0">
        {t('history.empty.title')}
      </p>
      <p className="text-sm text-gray-light-600 my-0 whitespace-pre-line text-center">
        {t('history.empty.description')}
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// HistoryDrawer
// ---------------------------------------------------------------------------

export interface HistoryDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Defaults to t('history.title') */
  title?: string
  entries: EntityAuditLogEntry[]
  isLoading: boolean
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
  fetchNextPage?: () => void
  renderDescription?: (entry: EntityAuditLogEntry) => React.ReactNode
}

export const HistoryDrawer: React.FC<HistoryDrawerProps> = ({
  open,
  onOpenChange,
  title,
  entries,
  isLoading,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  renderDescription,
}) => {
  const { t } = useTranslation()
  const drawerTitle = title ?? t('history.title')

  const groups = useMemo(
    () => groupByDate(entries, t('history.today')),
    [entries, t]
  )

  const { scrollContainerRef, loadMoreRef } = useInfinityLoadMore({
    hasNextPage: !!hasNextPage,
    isFetchingNextPage: !!isFetchingNextPage,
    fetchNextPage: async () => {
      fetchNextPage?.()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return {} as any
    },
  })

  let bodyContent = null
  if (isLoading && entries.length === 0) {
    bodyContent = (
      <div className="flex items-center justify-center py-20">
        <Spinner className="size-7 text-brand-600" />
      </div>
    )
  } else if (entries.length === 0) {
    bodyContent = <EmptyState />
  } else {
    bodyContent = (
      <div className="px-6">
        {groups.map((group) => (
          <div key={group.key}>
            {/* Date separator */}
            <div className="flex items-center gap-3 py-4">
              <div className="flex-1 h-px bg-gray-light-200" />
              <span className="text-xs font-medium text-secondary-500 flex-shrink-0">
                {group.label}
              </span>
              <div className="flex-1 h-px bg-gray-light-200" />
            </div>

            {/* Cards */}
            <div className="divide-y divide-gray-light-100">
              {group.entries.map((entry) => (
                <AuditCard
                  key={entry.id}
                  entry={entry}
                  isToday={group.isToday}
                  renderDescription={renderDescription}
                />
              ))}
            </div>
          </div>
        ))}

        {/* Loading more indicator — shown while fetching next page */}
        {isFetchingNextPage && (
          <div className="flex items-center justify-center py-4">
            <Spinner className="size-5 text-brand-600" />
          </div>
        )}

        {/* Invisible element for IntersectionObserver */}
        <div ref={loadMoreRef} className="h-1" />
      </div>
    )
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="right">
      <DrawerContent className="w-history-drawer sm:max-w-history-drawer flex flex-col" data-vaul-no-drag>
        {/* Header */}
        <DrawerHeader className="flex flex-row items-center justify-between border-b border-secondary flex-shrink-0 pb-4">
          <DrawerTitle>{drawerTitle}</DrawerTitle>
          <Button
            variant="text"
            className="w-7 h-7 p-0 text-gray-400 hover:text-gray-600 bg-transparent hover:bg-transparent border-none"
            onClick={() => onOpenChange(false)}
          >
            <X size={18} />
          </Button>
        </DrawerHeader>

        {/* Body */}
        <div className="flex-1 overflow-y-auto mt-3" ref={scrollContainerRef}>
          {bodyContent}
        </div>
      </DrawerContent>
    </Drawer>
  )
}
